// What the calls part's server and screen share: limits, the shapes of the
// REST answers, the signalling messages over /api/voice and their close codes.
// No dependencies, so the screen imports it as
// `@trameria/cloud-api/voice-shared` without pulling server code in.

/** A call holds at most this many people (a mesh of direct connections). */
export const VOICE_ROOM_MAX_PARTICIPANTS = 4;
export const VOICE_TITLE_MAX = 100;
/** The ended calls a list keeps (every call in progress comes too). */
export const VOICE_ENDED_LIMIT = 50;

/** Where the signalling socket is (`?workspace=<slug>&room=<id>[&resume=<connection>]`). */
export const VOICE_PATH = "/api/voice";

/**
 * How long a stay waits for its socket to come back after the way broke
 * (the screen stops trying a little before).
 */
export const VOICE_RESUME_MS = 30_000;

/**
 * Why the server closed the signalling socket. Other codes (1006, 1012 when a
 * node stops, ...) mean the way broke: the screen connects again and resumes.
 */
export const VOICE_CLOSE = {
  /** The same person joined from another tab or device. */
  replaced: 4001,
  /** Calls are off in the space for someone who was not in this call. */
  disabled: 4403,
  /** No such call, or one the person cannot see (any more). */
  notFound: 4404,
  /** The call ended. */
  ended: 4409,
  /** The call is full. */
  full: 4429,
} as const;

/**
 * What the screen closes a socket with when it opens another one right away
 * (to make its connections again): the stay is kept for the new socket. A
 * clean close (1000) is leaving.
 */
export const VOICE_REJOIN_CLOSE = 4000;

/** The codes after which the screen does not connect again. */
export const VOICE_FINAL_CLOSES: ReadonlySet<number> = new Set(
  Object.values(VOICE_CLOSE),
);

export interface VoiceParticipantView {
  userId: string;
  joinedAt: string;
  leftAt: string | null;
}

export interface VoiceRoomView {
  id: string;
  spaceId: string;
  title: string;
  createdByUserId: string | null;
  createdAt: string;
  endedAt: string | null;
  /** Everyone who was in it, oldest first (one row per stay). */
  participants: VoiceParticipantView[];
}

export interface IceServerView {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface IceServersView {
  iceServers: IceServerView[];
}

export interface VoiceTeamEvents {
  "voice_room.created": { id: string; spaceId: string };
  "voice_room.changed": { id: string; spaceId: string };
  "voice_room.ended": { id: string; spaceId: string };
}

// ---- signalling ---------------------------------------------------------------

export interface VoiceDescription {
  type: "offer" | "answer" | "pranswer" | "rollback";
  sdp?: string;
}

export interface VoiceCandidate {
  candidate?: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
  usernameFragment?: string | null;
}

export type VoiceSignalData =
  | { description: VoiceDescription }
  | { candidate: VoiceCandidate | null };

export type VoiceClientMessage =
  | { type: "signal"; to: string; data: VoiceSignalData }
  | { type: "mute"; muted: boolean }
  | { type: "screen"; sharing: boolean }
  /** Leaving on purpose (the leave button, the tab going away). */
  | { type: "leave" };

export interface VoicePeerInfo {
  userId: string;
  /**
   * Their stay (a new one when they joined again): a screen that resumed sees
   * whether the person it is connected to is still the same stay.
   */
  stay: string;
  muted: boolean;
  sharing: boolean;
}

export type VoiceServerMessage =
  | {
      type: "hello";
      self: string;
      /** This socket's id; connecting again with `resume=<it>` keeps the place. */
      connection: string;
      /** True when this socket took over a stay that was already there (peers kept). */
      resumed: boolean;
      peers: VoicePeerInfo[];
    }
  | ({ type: "peer.joined" } & VoicePeerInfo)
  | { type: "peer.left"; userId: string }
  | { type: "peer.muted"; userId: string; muted: boolean }
  | { type: "peer.screen"; userId: string; sharing: boolean }
  | { type: "signal"; from: string; data: VoiceSignalData };
